import { useState, useEffect, useCallback } from 'react';
import { Users } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { muze } from '../lib/moduly';
import TabBar from '../components/layout/TabBar';
import { REZERVA_LISTY } from '../components/MobilniLista';
import { SKLO_SVETLE } from '../lib/uiTokens';
import { SECTION_COLOR, SECTION_BG } from '../components/zamestnanci/lide';
import Organizace from '../components/zamestnanci/organizace/Organizace';
import OsobniDokumenty from '../components/zamestnanci/dokumenty/OsobniDokumenty';
import Kvalifikace from '../components/zamestnanci/kvalifikace/Kvalifikace';
import SpravaSkoleni from '../components/zamestnanci/skoleni/SpravaSkoleni';
import Hodnoceni from '../components/zamestnanci/Hodnoceni';
import Kariery from '../components/zamestnanci/kariery/Kariery';
import NastaveniModulu, { NastaveniTlacitko } from '../components/layout/NastaveniModulu';
import NastaveniHodnoty from '../components/layout/NastaveniHodnoty';
import { TypyUvazku, Pozice, Oddeleni, TypyDokumentu, Dovednosti, Certifikaty, KpiKatalog, Pojistovny, StatniPrislusnosti } from '../components/nastaveni/Ciselniky';

// ⚙ Nastavení Lidí → Nástup (hodnoty z cis_nastaveni)
const POLE_NASTUPU = [
    { klic: 'email_priprava_smlouvy', nazev: 'E-mail personalisty', typ: 'email', popis: 'Sem chodí podklady k přípravě smlouvy, když nový člověk dokončí nástup.' },
    { klic: 'pozvanka_platnost_dni', nazev: 'Platnost pozvánky (dní)', typ: 'number', vychozi: 7, popis: 'Jak dlouho platí první PIN z pozvánky.' },
];


/**
 * Modul **Lidé** (id `zamestnanci`) — HR modul OS.
 *
 * Záložky z katalogu: Organizace · Osobní dokumenty · Kvalifikace · Školení ·
 * Hodnocení (připravuje se). Od 2026-09-28 sem patří i bývalé moduly
 * Vzdělávání a Hodnocení; vlastní školení má zaměstnanec v Cockpitu.
 *
 * Stránka drží jen obal (hlavička, záložky) a data, která sdílí víc záložek:
 * seznam lidí, typy certifikátů, stav nástupu a dokumenty (kvůli pohledu
 * „Nepodepsáno" a „K dořešení" v Organizaci). Rozvržení záložek je norma
 * (`standard-rozvrzeni.md`): na mobilu připnuté k výřezu, edge-to-edge.
 */
export default function Zamestnanci({ user, initialTab = null, navParams = null }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
    useEffect(() => {
        const h = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, []);

    const [activeTab, setActiveTab] = useState(initialTab);
    // Proklik odjinud (notifikace, stará adresa Vzdělávání) přepne záložku
    // i tehdy, když je modul už otevřený.
    useEffect(() => { if (initialTab) setActiveTab(initialTab); }, [initialTab]);

    const upravujeSeznam = muze(user, 'zamestnanci.seznam', 'upravuje');
    const vidiDokumenty = muze(user, 'zamestnanci.dokumenty');
    const upravujeDokumenty = muze(user, 'zamestnanci.dokumenty', 'upravuje');
    const upravujeKvalifikace = muze(user, 'zamestnanci.kvalifikace', 'upravuje');
    const isManager = !upravujeSeznam && !!user?.has_subordinates;
    const isSuperadmin = user?.role === 'superadmin';
    const upravujeKariery = muze(user, 'zamestnanci.kariery', 'upravuje');
    const [nastaveni, setNastaveni] = useState(false);
    const cis = { isDark, barva: SECTION_COLOR };
    // ⚙ Nastavení Lidí — sekce podle toho, co člověk smí měnit (norma §1h)
    const sekceNastaveni = [
        upravujeSeznam && { id: 'uvazky', nazev: 'Mzdové parametry', popis: 'typy úvazků, odvody, limit', obsah: () => <TypyUvazku {...cis} /> },
        upravujeSeznam && { id: 'pozice', nazev: 'Pozice', obsah: () => <Pozice {...cis} /> },
        upravujeSeznam && { id: 'oddeleni', nazev: 'Oddělení', obsah: () => <Oddeleni {...cis} /> },
        upravujeDokumenty && { id: 'typy-dokumentu', nazev: 'Typy osobních dokumentů', popis: 'podpisy, povolené soubory', obsah: () => <TypyDokumentu {...cis} /> },
        upravujeKvalifikace && { id: 'dovednosti', nazev: 'Dovednosti', popis: 'katalog pro kvalifikace a kariéry', obsah: () => <Dovednosti {...cis} /> },
        upravujeKvalifikace && { id: 'certifikaty', nazev: 'Certifikáty', popis: 'typy, platnost, odznak', obsah: () => <Certifikaty {...cis} /> },
        upravujeKariery && { id: 'kpi', nazev: 'KPI katalog', popis: 'KPI pro bonusy podle kariér', obsah: () => <KpiKatalog {...cis} /> },
        upravujeSeznam && { id: 'pojistovny', nazev: 'Pojišťovny', obsah: () => <Pojistovny {...cis} /> },
        upravujeSeznam && { id: 'prislusnosti', nazev: 'Státní příslušnosti', obsah: () => <StatniPrislusnosti {...cis} /> },
        upravujeSeznam && { id: 'nastup', nazev: 'Nástup', popis: 'e-mail personalisty, pozvánky', obsah: () => <NastaveniHodnoty pole={POLE_NASTUPU} isDark={isDark} /> },
    ].filter(Boolean);

    // ── Sdílená data ─────────────────────────────────────────
    const [zamestnanci, setZamestnanci] = useState([]);
    const [loading, setLoading] = useState(true);
    const [certTypy, setCertTypy] = useState([]);
    const [onboardingMap, setOnboardingMap] = useState({});
    const [dokumenty, setDokumenty] = useState(null);
    const [dokumentyChyba, setDokumentyChyba] = useState(null);

    const nactiLidi = useCallback(() => fetch('/api/zamestnanci').then(r => r.json())
        .then(d => { setZamestnanci(Array.isArray(d) ? d : []); setLoading(false); })
        .catch(() => setLoading(false)), []);

    const nactiOnboarding = useCallback(() => {
        if (!upravujeSeznam && !isManager) return;
        fetch('/api/zamestnanci/onboarding-prehled').then(r => r.json())
            .then(d => setOnboardingMap(Object.fromEntries((Array.isArray(d) ? d : []).map(o => [o.id, o.checklist]))))
            .catch(() => {});
    }, [upravujeSeznam, isManager]);

    const nactiDokumenty = useCallback(() => {
        if (!vidiDokumenty) return;
        setDokumentyChyba(null);
        fetch('/api/dokumenty')
            .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
            .then(d => setDokumenty(Array.isArray(d) ? d : []))
            .catch(e => { setDokumentyChyba(e.message); setDokumenty(prev => prev || []); });
    }, [vidiDokumenty]);

    useEffect(() => {
        nactiLidi();
        nactiOnboarding();
        nactiDokumenty();
        fetch('/api/certifikace').then(r => r.json()).then(d => setCertTypy(Array.isArray(d) ? d : [])).catch(() => {});
    }, [nactiLidi, nactiOnboarding, nactiDokumenty]);

    const poZmene = () => { nactiLidi(); nactiOnboarding(); nactiDokumenty(); };

    // Kvalifikace: kdo ji upravuje, pracuje se všemi; vedoucí jen se svými
    // podřízenými (celý podstrom, ne jen přímí).
    const podrizeniKvalifikace = (() => {
        if (upravujeKvalifikace) return 'all';
        if (!user?.has_subordinates || !user?.zamestnanec_id) return [];
        const ids = [];
        const projdi = (nadrizenyId) => zamestnanci.filter(z => z.nadrizeny_id === nadrizenyId)
            .forEach(z => { ids.push(z.id); projdi(z.id); });
        projdi(user.zamestnanec_id);
        return ids;
    })();

    // ── Vzhled ───────────────────────────────────────────────
    const mutedColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const cardStyle = isDark ? {
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 0 0 0.5px rgba(255,255,255,0.03)',
    } : SKLO_SVETLE;
    const inputStyle = {
        width: '100%', height: 32, borderRadius: 8, padding: '0 10px', outline: 'none',
        background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)', colorScheme: isDark ? 'dark' : 'light',
    };

    // Záložka se seznamem: na mobilu připnutá k výřezu (norma §1b), na desktopu na výšku obsahu
    const pripnuta = isMobile
        ? { position: 'fixed', zIndex: 0, top: 'var(--hlavicka-mobil)', left: 0, right: 0, bottom: 0, overflow: 'hidden' }
        : { height: '100%', minHeight: 0 };
    // Záložky bez `ModulLayout` (Kvalifikace, Školení, Hodnocení) scrollují samy
    const rolujici = isMobile
        ? { ...pripnuta, overflowY: 'auto', padding: `12px 14px ${REZERVA_LISTY}` }
        : { height: '100%', minHeight: 0, overflowY: 'auto', paddingBottom: 24 };

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ flexShrink: 0 }}>
                {!isMobile && (
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 7, background: SECTION_BG }}>
                            <Users size={20} style={{ color: SECTION_COLOR }} />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Lidé</h1>
                            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                {loading ? 'Načítám…' : `${zamestnanci.length} ${zamestnanci.length === 1 ? 'člověk' : zamestnanci.length < 5 ? 'lidé' : 'lidí'}`} · dokumenty, kvalifikace, školení a kariéry
                            </p>
                        </div>
                        {sekceNastaveni.length > 0 && <NastaveniTlacitko onClick={() => setNastaveni(true)} barva={SECTION_COLOR} isDark={isDark} popis="Nastavení Lidí" />}
                    </div>
                )}
                <TabBar modul="zamestnanci" user={user} aktivni={activeTab} onZmena={setActiveTab}
                    barva={SECTION_COLOR} isDark={isDark} isMobile={isMobile} />
            </div>

            <div style={{ flex: 1, minHeight: 0, paddingTop: isMobile ? 0 : 16, display: 'flex', flexDirection: 'column' }}>
                {activeTab === 'seznam' && (
                    <div className="flex flex-col" style={pripnuta}>
                        <Organizace user={user} isDark={isDark} isMobile={isMobile} cardStyle={cardStyle} inputStyle={inputStyle} mutedColor={mutedColor}
                            zamestnanci={zamestnanci} loading={loading} onZmena={poZmene}
                            certTypy={certTypy} onboardingMap={onboardingMap} dokumenty={dokumenty}
                            vidiDokumenty={vidiDokumenty} upravujeSeznam={upravujeSeznam} upravujeDokumenty={upravujeDokumenty} isManager={isManager}
                            rezervaDole={isMobile ? REZERVA_LISTY : null} vybranyZvenku={navParams?.zamestnanecId ?? null} />
                    </div>
                )}
                {activeTab === 'dokumenty' && vidiDokumenty && (
                    <div className="flex flex-col" style={pripnuta}>
                        <OsobniDokumenty isDark={isDark} isMobile={isMobile} cardStyle={cardStyle} inputStyle={inputStyle} mutedColor={mutedColor}
                            zamestnanci={zamestnanci} dokumenty={dokumenty} loading={dokumenty === null} chyba={dokumentyChyba}
                            onZmena={nactiDokumenty} upravujeDokumenty={upravujeDokumenty} isSuperadmin={isSuperadmin}
                            rezervaDole={isMobile ? REZERVA_LISTY : null} />
                    </div>
                )}
                {activeTab === 'kvalifikace' && (
                    <div className="flex flex-col" style={pripnuta}>
                        <Kvalifikace isDark={isDark} isMobile={isMobile} cardStyle={cardStyle} inputStyle={inputStyle} mutedColor={mutedColor}
                            editableIds={podrizeniKvalifikace} rezervaDole={isMobile ? REZERVA_LISTY : null} />
                    </div>
                )}
                {activeTab === 'skoleni' && (
                    <div className="flex flex-col" style={pripnuta}>
                        <SpravaSkoleni isDark={isDark} isMobile={isMobile} cardStyle={cardStyle} inputStyle={inputStyle} mutedColor={mutedColor}
                            zamestnanci={zamestnanci} certTypy={certTypy} initialSkoleniId={navParams?.skoleniId ?? null}
                            rezervaDole={isMobile ? REZERVA_LISTY : null} />
                    </div>
                )}
                {activeTab === 'kariery' && (
                    <div className="flex flex-col" style={pripnuta}>
                        <Kariery zamestnanci={zamestnanci} upravuje={upravujeKariery} isDark={isDark} isMobile={isMobile} cardStyle={cardStyle}
                            inputStyle={inputStyle} mutedColor={mutedColor} rezervaDole={isMobile ? REZERVA_LISTY : null} />
                    </div>
                )}
                {activeTab === 'hodnoceni' && (
                    <div style={rolujici}>
                        <Hodnoceni isDark={isDark} />
                    </div>
                )}
            </div>
            {nastaveni && <NastaveniModulu nadpis="Nastavení Lidí" sekce={sekceNastaveni} barva={SECTION_COLOR} isDark={isDark} onClose={() => setNastaveni(false)} />}
        </div>
    );
}
